import { useState } from "react";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getSupabase } from "@/integrations/supabase/lazy";
import { toast } from "sonner";

interface BackInStockAlertProps {
  productHandle: string;
  productTitle: string;
}

export const BackInStockAlert = ({ productHandle, productTitle }: BackInStockAlertProps) => {
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;

    // Bot filled the hidden field — pretend success, store nothing.
    if (honeypot) {
      setSubmitted(true);
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Please enter a valid email address", { position: "top-center" });
      return;
    }

    setLoading(true);
    try {
      const supabase = await getSupabase();
      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase.from("inventory_alerts").insert({
        user_id: user?.id ?? null,
        product_handle: productHandle,
        product_title: productTitle,
        email: trimmed,
        alert_type: "back_in_stock",
      });
      if (error) throw error;

      setSubmitted(true);
      toast.success("Added to the waitlist", { position: "top-center" });

    } catch {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };


  if (submitted) {
    return (
      <div className="flex items-center gap-2 p-3 bg-primary/5 border border-primary/20 text-sm text-primary">
        <Bell className="h-4 w-4 flex-shrink-0" />
        <span>You're on the waitlist for this piece.</span>
      </div>
    );
  }


  return (
    <div className="p-4 border border-border bg-secondary/20">
      <div className="flex items-center gap-2 mb-1">
        <Bell className="h-4 w-4 text-foreground/60" />
        <span className="text-sm font-medium text-foreground">Join the waitlist</span>
      </div>
      <p className="text-sm text-foreground/70 mb-3">Tell us you want this piece and we'll note your interest.</p>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <div className="absolute w-px h-px overflow-hidden -left-[9999px]" aria-hidden="true">
          <label htmlFor="bis-website">Website</label>
          <input
            id="bis-website"
            name="website"
            type="text"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>
        <label htmlFor="bis-email" className="sr-only">Email address</label>
        <input
          id="bis-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email"
          required
          className="flex-1 px-3 py-2 text-sm border border-border bg-background focus:outline-none focus:border-primary transition-colors"
        />

        <Button
          type="submit"
          disabled={loading}
          className="rounded-full text-xs uppercase tracking-wider font-bold px-4"
        >
          Join waitlist
        </Button>
      </form>
    </div>
  );

};
